import { Clock, MousePointerClick, Eye, Wind } from 'lucide-react';
import { OrganicShapes } from './decor/OrganicShapes';
import { Reveal } from './Reveal';

const problems = [
  {
    icon: Clock,
    label: 'Lag',
    text: 'Risk and quality are measured after the year closes. Information arrives too late to act on.',
  },
  {
    icon: MousePointerClick,
    label: 'Friction',
    text: 'Software burdens the clinician by adding clicks between them and the patient.',
  },
];

const answers = [
  {
    icon: Eye,
    label: 'Foresight in',
    text: 'Every gap surfaces with a clear next step while there is still time to act.',
  },
  {
    icon: Wind,
    label: 'Friction out',
    text: 'The clinician seamlessly approves the next move rather than hunting for it.',
  },
];

export function Thesis() {
  return (
    <section className="relative overflow-hidden py-24 bg-[#f9fbfc]">
      <OrganicShapes variant="soft" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-14">
          <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold">
            Our Thesis
          </span>
          <h2 className="mt-6 text-4xl lg:text-5xl text-[#1f2937] leading-tight">
            Two things get in the way of care.
          </h2>
          <p className="mt-5 text-xl text-[#4B5563] leading-relaxed">
            By the time the numbers lock, the year is already lost. We flip that from hindsight to
            foresight built on <span className="text-[#1f2937] font-medium">one unified source of truth</span>.
          </p>
        </Reveal>

        <div className="ph-thesis-grid">
          {/* Problems */}
          <div className="ph-stack">
            {problems.map((p, index) => {
              const Icon = p.icon;
              return (
                <Reveal as="div" key={p.label} direction="right" delay={index * 120} className="rounded-2xl bg-white border border-[#4B5563]/10 p-6 flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[#4B5563]/10 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Icon className="h-5 w-5 text-[#4B5563]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#4B5563]/60 mb-1">{p.label}</p>
                    <p className="text-[#4B5563] leading-relaxed">{p.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Transition */}
          <div className="hidden lg:flex items-center justify-center px-2">
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #34AFFB 0%, #70D6C5 100%)', boxShadow: '0 8px 20px -6px rgba(52,175,251,0.5)' }}
            >
              <span className="text-white text-lg leading-none">→</span>
            </div>
          </div>

          {/* Answers */}
          <div className="ph-stack">
            {answers.map((a, index) => {
              const Icon = a.icon;
              return (
                <Reveal
                  as="div"
                  key={a.label}
                  direction="left"
                  delay={index * 120}
                  className="rounded-2xl p-6 flex items-start gap-4 text-white"
                  style={{ background: 'linear-gradient(135deg, #34AFFB 0%, #70D6C5 100%)' }}
                >
                  <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/75 mb-1">{a.label}</p>
                    <p className="text-white/95 leading-relaxed">{a.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
